import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Share2, Copy, Check, Trash2, Lock, Eye, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';

const ShareDialog = ({ open, onOpenChange, docId, docTitle }) => {
  const { getAuthHeaders } = useAuth();
  const [expiresHours, setExpiresHours] = useState('72');
  const [customHours, setCustomHours] = useState('');
  const [permission, setPermission] = useState('view');
  const [password, setPassword] = useState('');
  const [maxAccesses, setMaxAccesses] = useState('');
  const [creating, setCreating] = useState(false);
  const [latestShare, setLatestShare] = useState(null);
  const [shares, setShares] = useState([]);
  const [copied, setCopied] = useState(false);

  const loadShares = useCallback(async () => {
    if (!docId) return;
    try {
      const res = await api.listShareLinks(docId, getAuthHeaders());
      setShares(res.data?.shares || []);
    } catch (e) {
      // 403 = non-owner, just hide list
      setShares([]);
    }
  }, [docId, getAuthHeaders]);

  useEffect(() => {
    if (open) loadShares();
  }, [open, loadShares]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const hours = expiresHours === 'custom' ? parseInt(customHours, 10) : parseInt(expiresHours, 10);
      if (!hours || hours <= 0) {
        toast.error('Enter a valid expiry');
        setCreating(false);
        return;
      }
      const body = {
        expires_hours: hours,
        permission,
        password: password || null,
        max_accesses: maxAccesses ? parseInt(maxAccesses, 10) : null
      };
      const res = await api.createShareLink(docId, body, getAuthHeaders());
      setLatestShare(res.data);
      toast.success('Share link created');
      loadShares();
      // Reset password / max but keep duration + permission for convenience
      setPassword('');
      setMaxAccesses('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Revoke this share link? The URL will stop working immediately.')) return;
    try {
      await api.revokeShareLink(shareId, getAuthHeaders());
      toast.success('Link revoked');
      loadShares();
      if (latestShare?.id === shareId) setLatestShare(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Link copied to clipboard');
    } catch (e) {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const formatExpiry = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const diffMs = d.getTime() - Date.now();
      const hours = Math.round(diffMs / 36e5);
      if (hours < 0) return `expired ${Math.abs(hours)}h ago`;
      if (hours < 48) return `in ${hours}h`;
      return `in ${Math.round(hours / 24)}d`;
    } catch (e) { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" /> Share "{docTitle}"
          </DialogTitle>
          <DialogDescription>
            Create a tokenised, time-limited link. The link itself is the credential — no recipient account needed.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Expires</label>
            <Select value={expiresHours} onValueChange={setExpiresHours}>
              <SelectTrigger data-testid="share-expires-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 hour</SelectItem>
                <SelectItem value="24">24 hours</SelectItem>
                <SelectItem value="72">72 hours (3 days)</SelectItem>
                <SelectItem value="168">7 days</SelectItem>
                <SelectItem value="720">30 days</SelectItem>
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {expiresHours === 'custom' && (
              <Input
                type="number"
                min="1"
                max="720"
                placeholder="Hours (1-720)"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                className="mt-2"
                data-testid="share-custom-hours"
              />
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Permission</label>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger data-testid="share-permission-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="view">View only</SelectItem>
                <SelectItem value="download">Allow download</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Password (optional)</label>
            <Input
              type="password"
              placeholder="Recipients will be asked for this"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="share-password"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Max accesses (optional)</label>
            <Input
              type="number"
              min="1"
              placeholder="e.g. 5"
              value={maxAccesses}
              onChange={(e) => setMaxAccesses(e.target.value)}
              data-testid="share-max-accesses"
            />
          </div>
        </div>

        <Button onClick={handleCreate} disabled={creating} data-testid="share-generate-btn" className="w-full">
          {creating ? 'Generating…' : 'Generate link'}
        </Button>

        {latestShare && (
          <div className="border border-blue-200 bg-blue-50 rounded-md p-3" data-testid="share-result">
            <p className="text-xs font-semibold text-blue-800 mb-2">New link (copy now — token is shown only here)</p>
            <div className="flex gap-2">
              <Input value={`${window.location.origin}/shared?token=${latestShare.token}`} readOnly data-testid="share-result-url" />
              <Button
                type="button"
                variant="outline"
                onClick={() => copyToClipboard(`${window.location.origin}/shared?token=${latestShare.token}`)}
                data-testid="share-copy-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-blue-700 mt-2">
              Expires {formatExpiry(latestShare.expires_at)} · permission: {latestShare.permission}
              {latestShare.password_protected ? ' · password-protected' : ''}
            </p>
          </div>
        )}

        <div data-testid="share-list">
          <p className="text-xs font-semibold text-slate-600 mb-2">Active links for this document</p>
          {shares.length === 0 ? (
            <p className="text-xs text-slate-400">No active share links</p>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-2 py-1.5">Token</th>
                    <th className="text-left px-2 py-1.5">Perm</th>
                    <th className="text-left px-2 py-1.5">Expires</th>
                    <th className="text-right px-2 py-1.5">Accessed</th>
                    <th className="text-right px-2 py-1.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {shares.map((s) => (
                    <tr key={s.id} className={`border-t border-slate-100 ${s.is_revoked ? 'opacity-50' : ''}`} data-testid={`share-row-${s.id}`}>
                      <td className="px-2 py-1.5 font-mono">{s.token_preview}{s.password_protected ? ' 🔒' : ''}</td>
                      <td className="px-2 py-1.5">
                        {s.permission === 'download' ? <Download className="w-3 h-3 inline" /> : <Eye className="w-3 h-3 inline" />} {s.permission}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">{formatExpiry(s.expires_at)}</td>
                      <td className="px-2 py-1.5 text-right">{s.accessed_count}{s.max_accesses ? ` / ${s.max_accesses}` : ''}</td>
                      <td className="px-2 py-1.5 text-right">
                        {!s.is_revoked && (
                          <button
                            onClick={() => handleRevoke(s.id)}
                            className="text-slate-400 hover:text-red-600"
                            data-testid={`share-revoke-${s.id}`}
                            title="Revoke"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {s.is_revoked && <span className="text-red-500 text-[10px]">revoked</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
